// DOCSBUG4 (docs/build-briefs/DOCSBUG4-reverse-a-movement.md, DEC-274): the
// pure half of the "Recent movements" list on the Edit bottle sheet's Stock
// & vendor tab. Everything that decides WHAT a row says lives here, with no
// DOM and no fetch, so a plain node:test file can pin the wording (test/
// ui-movement-reversal.test.ts). ui/src/admin.ts owns the screen wiring.
//
// Two rules this module exists to hold:
//   - Every row states its own state before any tap: reversible, already
//     reversed, or cannot be reversed with the plain reason. The server's
//     reversal route refuses the last two (src/domain/inventoryMovements.ts's
//     reverseInventoryMovement), and this mirrors those refusals so the
//     screen never offers a button that can only fail.
//   - The reversal quantity is never typed or computed here for the server:
//     the route derives it by negating the row. What is computed here is
//     display only (the confirm sentence and the on-hand preview).

import type { InventoryMovementDto, StockHistoryDto } from "./api.ts";
import { bottleFigure } from "./usageWasteModel.ts";

export type MovementState = "reversible" | "reversed" | "cannot" | "history";

/** INV41: a row is either a manual movement (reversible by rule) or a receipt, sale or count result (history only). */
export type MovementRowType = StockHistoryDto["kind"];

export interface MovementRowView {
  id: string;
  type: MovementRowType;
  typeLabel: string;
  /** ISO time, so manual movements and history rows can be merged newest first. */
  createdAt: string;
  whenText: string;
  detail: string;
  state: MovementState;
  /** Signed, e.g. "−2 bottles". Struck through by the screen when reversed. */
  quantityPrimary: string;
  /** Ounces beneath the bottle figure, or "" when the primary is already ounces. */
  quantitySecondary: string;
}

export interface MovementViewContext {
  /** Ounces in one bottle, or null when the item has no convertible size. */
  bottleOunces: number | null;
  /** The people the sheet knows, by id. A missing id reads "Someone". */
  actorNameById: ReadonlyMap<string, string>;
  /** "Sep 30, 2:14 PM" */
  formatDateTime: (iso: string) => string;
  /** "Sep 30" */
  formatDate: (iso: string) => string;
  /** Reversal rows made on this screen in this open, shown as "Just now". */
  freshReversalIds: ReadonlySet<string>;
}

/** What a reversal from before a reason was required says in its row. */
export const NO_REASON_RECORDED = "No reason recorded (before this change)";

const OUNCES_PER_BOTTLE_ML = 29.5735295625;
const MINUS = "−";

const TYPE_LABELS: Record<InventoryMovementDto["movement_type"], string> = {
  waste: "Waste",
  return_to_stock: "Return to stock",
  vendor_return: "Vendor return",
  reversal: "Reversal",
  opening_balance: "Opening balance",
  receipt_correction: "Receipt fix",
  transfer_out: "Transfer out",
  transfer_in: "Transfer in",
};

const HISTORY_LABELS = {
  receipt: "Received",
  sale: "Sold",
  sale_undone: "Sale undone",
  count_adjustment: "Count result",
} as const;

export function movementTypeLabel(type: InventoryMovementDto["movement_type"]): string {
  return TYPE_LABELS[type];
}

/** The same US fluid ounce the server converts through. Null for a size unit
 * this screen cannot convert, which makes the rows show their stored unit. */
export function ouncesPerBottle(sizeValue: string | null, sizeUnit: string | null): number | null {
  if (sizeValue === null || sizeUnit === null) {
    return null;
  }
  const value = Number(sizeValue);
  if (!Number.isFinite(value) || value <= 0) {
    return null;
  }
  switch (sizeUnit) {
    case "oz":
      return value;
    case "ml":
      return value / OUNCES_PER_BOTTLE_ML;
    case "l":
      return (value * 1000) / OUNCES_PER_BOTTLE_ML;
    default:
      return null;
  }
}

/** UX78: the shared UX76 bottle rule (two decimals under 0.1 bottle, one
 * decimal otherwise), with a trailing ".0" dropped so whole bottles read
 * "2 bottles". Never a "-0": the sign is added by the caller, and only to a
 * figure that is not zero. */
function bottleCount(bottles: number): string {
  const figure = bottleFigure(String(bottles));
  return figure.endsWith(".0") ? figure.slice(0, -2) : figure;
}

function trimmedOneDecimal(value: number): string {
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

function ouncesText(ounces: number): string {
  const rounded = Math.round(Math.abs(ounces) * 10) / 10;
  return `${rounded.toFixed(1)} oz`;
}

function unitWord(unit: string, magnitudeText: string): string {
  const one = magnitudeText === "1";
  switch (unit) {
    case "bottle":
      return one ? "bottle" : "bottles";
    case "can":
      return one ? "can" : "cans";
    case "keg":
      return one ? "keg" : "kegs";
    case "unit":
      return one ? "unit" : "units";
    default:
      return unit;
  }
}

/** An unsigned quantity in words: "2 bottles", "1 bottle", or "5.0 oz" when
 * the item has no bottle size. `ounces` is the absolute stored size. */
export function quantityWords(
  magnitude: number,
  unit: string,
  bottleOunces: number | null,
): { primary: string; secondary: string } {
  if (unit === "oz") {
    if (bottleOunces !== null) {
      const bottles = bottleCount(magnitude / bottleOunces);
      return {
        primary: `${bottles} ${unitWord("bottle", bottles)}`,
        secondary: ouncesText(magnitude),
      };
    }
    return { primary: ouncesText(magnitude), secondary: "" };
  }
  const text = trimmedOneDecimal(magnitude);
  return { primary: `${text} ${unitWord(unit, text)}`, secondary: "" };
}

function signedWords(
  delta: number,
  unit: string,
  bottleOunces: number | null,
): { primary: string; secondary: string } {
  const words = quantityWords(Math.abs(delta), unit, bottleOunces);
  // A figure that rounds to nothing carries no sign (never "-0").
  const shownZero = Number(words.primary.split(" ")[0]) === 0;
  const sign = shownZero ? "" : delta < 0 ? MINUS : delta > 0 ? "+" : "";
  return { primary: `${sign}${words.primary}`, secondary: words.secondary };
}

export function classifyMovement(
  row: InventoryMovementDto,
  reversalOf: ReadonlyMap<string, InventoryMovementDto>,
): MovementState {
  if (
    row.movement_type === "reversal" ||
    row.movement_type === "receipt_correction" ||
    row.movement_type === "transfer_out" ||
    row.movement_type === "transfer_in"
  ) {
    return "cannot";
  }
  if (reversalOf.has(row.id)) {
    return "reversed";
  }
  if (Number(row.quantity_delta) === 0) {
    return "cannot";
  }
  return "reversible";
}

/** The reversal row that names each target, by the target's id. */
export function indexReversals(
  movements: readonly InventoryMovementDto[],
): Map<string, InventoryMovementDto> {
  const byTarget = new Map<string, InventoryMovementDto>();
  for (const row of movements) {
    if (row.movement_type === "reversal" && row.related_movement_id !== null) {
      byTarget.set(row.related_movement_id, row);
    }
  }
  return byTarget;
}

function lowerLabel(type: InventoryMovementDto["movement_type"]): string {
  return TYPE_LABELS[type].toLowerCase();
}

function actorName(row: InventoryMovementDto, context: MovementViewContext): string {
  return context.actorNameById.get(row.actor_employee_id) ?? "Someone";
}

function detailLine(
  row: InventoryMovementDto,
  state: MovementState,
  byId: ReadonlyMap<string, InventoryMovementDto>,
  reversalOf: ReadonlyMap<string, InventoryMovementDto>,
  context: MovementViewContext,
): string {
  const actor = actorName(row, context);
  if (row.movement_type === "receipt_correction") {
    return "Fix it with a new correction on the receipt.";
  }
  if (row.movement_type === "reversal") {
    const target = row.related_movement_id === null ? undefined : byId.get(row.related_movement_id);
    const what =
      target === undefined
        ? "an earlier movement"
        : `the ${lowerLabel(target.movement_type)} of ${context.formatDate(target.created_at)}`;
    // AU14-F14: why it was undone, as the label read then. A reversal from before
    // the reason was required says so, never a blank and never an invented one.
    return `Undid ${what} · ${row.action_reason_label_at_movement ?? NO_REASON_RECORDED} · ${actor}`;
  }
  if (row.movement_type === "transfer_out" || row.movement_type === "transfer_in") {
    // INVXFER1: a transfer is undone by cancelling it, never by a reversal.
    return actor;
  }
  if (state === "cannot") {
    // Only a zero-quantity row reaches here: the server refuses it with
    // "moved nothing, so there is nothing to reverse".
    return "It moved nothing, so there is nothing to undo.";
  }
  const parts: string[] = [];
  if (row.movement_type === "waste" && row.reason_label_at_movement !== null) {
    parts.push(row.reason_label_at_movement);
  } else if (row.movement_type === "vendor_return") {
    const vendor = row.vendor_name_at_movement;
    const note = row.note;
    if (vendor !== null && note !== null && note.length > 0) {
      parts.push(`To ${vendor}, ${note}`);
    } else if (vendor !== null) {
      parts.push(`To ${vendor}`);
    } else if (note !== null && note.length > 0) {
      parts.push(note);
    }
  } else if (row.note !== null && row.note.length > 0) {
    parts.push(row.note);
  }
  parts.push(actor);
  const reversal = reversalOf.get(row.id);
  if (reversal !== undefined) {
    parts.push(
      context.freshReversalIds.has(reversal.id)
        ? "Reversed just now"
        : `Reversed ${context.formatDate(reversal.created_at)}`,
    );
  }
  return parts.join(" · ");
}

export function buildMovementRows(
  movements: readonly InventoryMovementDto[],
  context: MovementViewContext,
): MovementRowView[] {
  const byId = new Map(movements.map((row) => [row.id, row] as const));
  const reversalOf = indexReversals(movements);
  return movements.map((row) => {
    const state = classifyMovement(row, reversalOf);
    const words = signedWords(Number(row.quantity_delta), row.unit, context.bottleOunces);
    return {
      id: row.id,
      type: row.movement_type,
      typeLabel: TYPE_LABELS[row.movement_type],
      createdAt: row.created_at,
      whenText: context.freshReversalIds.has(row.id)
        ? "Just now"
        : context.formatDateTime(row.created_at),
      detail: detailLine(row, state, byId, reversalOf, context),
      state,
      quantityPrimary: words.primary,
      quantitySecondary: words.secondary,
    };
  });
}

/** The one sentence the confirm leads with, built from the row. A row that
 * took stock out (negative) is added back; a row that put stock in is taken
 * back out. */
export function confirmSentence(
  row: InventoryMovementDto,
  productName: string,
  bottleOunces: number | null,
  formatDate: (iso: string) => string,
): string {
  const delta = Number(row.quantity_delta);
  const words = quantityWords(Math.abs(delta), row.unit, bottleOunces).primary;
  const label = TYPE_LABELS[row.movement_type];
  const date = formatDate(row.created_at);
  if (delta < 0) {
    return `Adds back ${words} of ${productName} removed as ${label} on ${date}.`;
  }
  return `Takes ${words} back out of stock, added as ${label} on ${date}.`;
}

/** What on hand reads after the reversal, in the unit on hand is shown in.
 * Null when the two units cannot be put on one scale, in which case the
 * screen shows only the figure it already has. Display only. */
export function projectedOnHand(
  onHandQuantity: string,
  onHandUnit: string,
  row: InventoryMovementDto,
  bottleOunces: number | null,
): { quantity: number; unit: string } | null {
  const onHand = Number(onHandQuantity);
  const reversalDelta = -Number(row.quantity_delta);
  if (!Number.isFinite(onHand) || !Number.isFinite(reversalDelta)) {
    return null;
  }
  if (row.unit === onHandUnit) {
    return { quantity: onHand + reversalDelta, unit: onHandUnit };
  }
  if (bottleOunces !== null && row.unit === "oz" && onHandUnit === "bottle") {
    return { quantity: onHand + reversalDelta / bottleOunces, unit: onHandUnit };
  }
  if (bottleOunces !== null && row.unit === "bottle" && onHandUnit === "oz") {
    return { quantity: onHand + reversalDelta * bottleOunces, unit: onHandUnit };
  }
  return null;
}

/** "This leaves on hand at -12.7 oz", for a reversal that takes on hand below
 * zero (allowed, DEC-274). Null when it does not. `figure` is the already
 * formatted projected on hand. */
export function negativeOnHandWarning(
  projected: { quantity: number } | null,
  figure: string,
): string | null {
  if (projected === null || Math.round(projected.quantity * 10) / 10 >= 0) {
    return null;
  }
  return `This leaves on\u00a0hand at ${figure}`;
}

/** The server's refusal for a movement that is already undone: "inventory
 * movement <id> has already been reversed". The screen shows its own plain
 * wording for this one (notes question 5), so it needs to recognise it. */
export function isAlreadyReversedRefusal(message: string | undefined): boolean {
  return message !== undefined && /has already been reversed/.test(message);
}

/** The domain's own refusal text without the movement id it leads with:
 * "inventory movement 3f2a... moved nothing, so there is nothing to reverse"
 * reads "This movement moved nothing, so there is nothing to reverse". Any
 * other message passes through with its first letter capitalized. The rows
 * never offer a button for these refusals, so this is the fallback for a row
 * that changed between loading the list and the tap. */
export function plainRefusal(message: string | undefined): string {
  if (message === undefined || message.length === 0) {
    return "That movement could not be reversed";
  }
  const withoutId = message.replace(/^inventory movement \S+ /, "This movement ");
  return withoutId.charAt(0).toUpperCase() + withoutId.slice(1);
}

/** INV41: the receipts, sales and count results from the stock history, as rows with no reverse action. The manual
 * movement kinds in the same feed are skipped: buildMovementRows already shows them with their reversal state. */
export function buildHistoryRows(
  entries: readonly StockHistoryDto[],
  context: MovementViewContext,
): MovementRowView[] {
  const rows: MovementRowView[] = [];
  for (const entry of entries) {
    if (entry.source === "movements") {
      continue;
    }
    const kind = entry.kind as keyof typeof HISTORY_LABELS;
    const counted = entry.quantity !== null && entry.countUnit !== null;
    // UIFIX5: under one bottle a count-unit figure reads in ounces ("1.5 oz"); a sold pour is never "0 bottles".
    const underOneBottle =
      counted &&
      entry.countUnit === "bottle" &&
      context.bottleOunces !== null &&
      Number(entry.quantity) !== 0 &&
      Math.abs(Number(entry.quantity)) < 1;
    const words = underOneBottle
      ? signedWords(Number(entry.quantity) * (context.bottleOunces ?? 0), "oz", null)
      : counted
        ? signedWords(Number(entry.quantity), entry.countUnit ?? "", null)
        : signedWords(Number(entry.quantityDelta), entry.unit, context.bottleOunces);
    const parts: string[] = [];
    if (entry.note !== null && entry.note.length > 0) {
      parts.push(entry.note);
    }
    if (entry.actorEmployeeId !== null) {
      parts.push(context.actorNameById.get(entry.actorEmployeeId) ?? "Someone");
    }
    rows.push({
      id: entry.id,
      type: entry.kind,
      typeLabel: HISTORY_LABELS[kind],
      createdAt: entry.createdAt,
      whenText: context.formatDateTime(entry.createdAt),
      detail: parts.join(" · "),
      state: "history",
      quantityPrimary: words.primary,
      quantitySecondary: words.secondary,
    });
  }
  return rows;
}
